import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { adminApi, formatApiError } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  LayoutDashboard, Users, Video, FileText, Settings, LogOut,
  Plus, Trash2, Edit, Save, X, Home, Calendar, CreditCard,
  MessageSquare, HelpCircle, UserCircle, Radio, BarChart2, ChevronDown, ChevronUp, Upload, Image, BookOpen
} from 'lucide-react';
import AdminSessionManager from '../components/admin/AdminSessionManager';

// Convert HEIC to JPEG blob if needed, then compress via canvas.
// Returns { dataUrl, lqip } — lqip is a tiny 20px thumbnail for blur-up placeholder.
async function compressImage(file, maxWidth = 1920, quality = 0.92) {
  let sourceFile = file;

  const isHeic = file.type === 'image/heic' || file.type === 'image/heif' ||
    /\.(heic|heif)$/i.test(file.name);

  if (isHeic) {
    const heic2any = (await import('heic2any')).default;
    const blobs = await heic2any({ blob: file, toType: 'image/jpeg', quality });
    const blob = Array.isArray(blobs) ? blobs[0] : blobs;
    sourceFile = new File([blob], file.name.replace(/\.(heic|heif)$/i, '.jpg'), { type: 'image/jpeg' });
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new window.Image();
      img.onload = () => {
        // Full-size image (WebP — 30% smaller than JPEG at same quality)
        const scale = Math.min(1, maxWidth / img.width);
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL('image/webp', quality);

        // LQIP: 20px-wide thumbnail — tiny enough to sit in the API response
        const lqipW = 20;
        const lqipH = Math.round(lqipW * img.height / img.width);
        const lqipCanvas = document.createElement('canvas');
        lqipCanvas.width = lqipW;
        lqipCanvas.height = lqipH;
        lqipCanvas.getContext('2d').drawImage(img, 0, 0, lqipW, lqipH);
        const lqip = lqipCanvas.toDataURL('image/jpeg', 0.5);

        resolve({ dataUrl, lqip });
      };
      img.onerror = reject;
      img.src = ev.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(sourceFile);
  });
}

// Reusable image upload field with compression + URL fallback
function ImageUploadField({ value, onChange, onLqipChange, label, maxWidth = 1920 }) {
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      console.log('[upload] step 1: compressing image…');
      const { dataUrl, lqip } = await compressImage(file, maxWidth);
      console.log('[upload] step 1 done, dataUrl length:', dataUrl.length);

      console.log('[upload] step 2: converting to blob…');
      const byteString = atob(dataUrl.split(',')[1]);
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);
      const blob = new Blob([ab], { type: 'image/webp' });
      console.log('[upload] step 2 done, blob size:', blob.size);

      console.log('[upload] step 3: getting presigned URL from backend…');
      const { data } = await adminApi.getUploadUrl();
      console.log('[upload] step 3 done, upload_url:', data.upload_url?.slice(0, 60));

      console.log('[upload] step 4: uploading to S3…');
      const s3Res = await fetch(data.upload_url, {
        method: 'PUT',
        body: blob,
        headers: { 'Content-Type': 'image/webp' },
      });
      if (!s3Res.ok) throw new Error(`S3 PUT failed: ${s3Res.status} ${s3Res.statusText}`);
      console.log('[upload] step 4 done — S3 upload successful');

      onChange(data.public_url);
      onLqipChange?.(lqip);
    } catch (err) {
      console.error('[upload] FAILED:', err);
      alert(`Failed to upload image: ${err.message}`);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleRemove = () => {
    onChange('');
    onLqipChange?.('');
  };

  const isBase64 = value?.startsWith('data:');
  const isS3 = value?.includes('s3.') || value?.startsWith('https://');

  return (
    <div className="space-y-2">
      {label && <Label>{label}</Label>}
      <div className="flex items-start gap-4">
        {/* Preview — fixed 96×96, always clipped */}
        <div className="shrink-0 w-24 h-24 rounded-lg border-2 border-dashed border-[#E7E5E4] bg-[#F4EFE6] overflow-hidden flex items-center justify-center">
          {value ? (
            <img src={value} alt="preview" className="w-full h-full object-cover" />
          ) : (
            <Image className="h-8 w-8 text-[#C8BFB5]" />
          )}
        </div>
        {/* Controls */}
        <div className="flex-1 space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <label className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer text-sm font-medium transition-colors border ${uploading ? 'bg-[#E7E5E4] text-[#A8A29E] border-[#E7E5E4] cursor-not-allowed' : 'bg-[#F4EFE6] hover:bg-[#E7DDD5] text-[#292524] border-[#E7E5E4]'}`}>
              <Upload className="h-4 w-4" />
              {uploading ? 'Uploading…' : 'Upload Photo'}
              <input type="file" accept="image/*,.heic,.heif" className="hidden" onChange={handleFile} disabled={uploading} />
            </label>
            {value && !uploading && (
              <button
                type="button"
                onClick={handleRemove}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 border border-red-200 transition-colors"
              >
                <X className="h-3.5 w-3.5" /> Remove
              </button>
            )}
          </div>
          <p className="text-xs text-[#A8A29E]">JPG, PNG, WEBP, HEIC · uploaded to S3 · max {maxWidth}px</p>
          {(isS3 && !isBase64) && (
            <p className="text-xs text-[#2C5E4B] font-medium">✓ Uploaded to S3</p>
          )}
          <div className="flex items-center gap-2">
            <div className="h-px flex-1 bg-[#E7E5E4]" />
            <span className="text-xs text-[#A8A29E]">or paste URL</span>
            <div className="h-px flex-1 bg-[#E7E5E4]" />
          </div>
          <Input
            placeholder="https://..."
            value={isBase64 ? '' : (value || '')}
            onChange={(e) => onChange(e.target.value)}
            className="text-sm"
          />
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/login?redirect=/admin');
      return;
    }
    if (user.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    setLoading(false);
  }, [user, navigate]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#292524]">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#9A3B26] border-t-transparent"></div>
      </div>
    );
  }

  const navGroups = [
    {
      items: [
        { id: 'overview', label: 'Overview', icon: LayoutDashboard },
        { id: 'content', label: 'Content', icon: FileText },
        { id: 'curriculum', label: 'Curriculum', icon: Calendar },
        { id: 'videos', label: 'Videos', icon: Video },
        { id: 'sessions', label: 'Live Sessions', icon: Radio },
        { id: 'testimonials', label: 'Testimonials', icon: MessageSquare },
        { id: 'faqs', label: 'FAQs', icon: HelpCircle },
        { id: 'instructors', label: 'Instructors', icon: UserCircle },
        { id: 'payments', label: 'Payments', icon: CreditCard },
        { id: 'settings', label: 'Settings', icon: Settings },
        { id: 'blog', label: 'Blog', icon: BookOpen },
      ],
    },
    {
      label: 'Students',
      items: [
        { id: 'users', label: 'Users', icon: Users },
        { id: 'progress', label: 'Progress', icon: BarChart2 },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex overflow-x-hidden">
      {/* Sidebar — desktop only; not designed for mobile */}
      <aside className="admin-sidebar w-64 flex-shrink-0 fixed h-full hidden md:flex md:flex-col">
        <div className="p-6 flex-shrink-0">
          <Link to="/" className="font-heading text-2xl font-bold text-white">
            Kalaripayattu
          </Link>
          <p className="text-white/60 text-sm mt-1">Admin Dashboard</p>
        </div>

        <nav className="px-4 flex-1 overflow-y-auto pb-4 space-y-4">
          {navGroups.map((group, gi) => (
            <div key={gi}>
              {group.label && (
                <div className="flex items-center gap-2 px-2 mb-1 mt-2">
                  <div className="flex-1 border-t border-white/20" />
                  <span className="text-white/40 text-xs font-semibold uppercase tracking-widest whitespace-nowrap">
                    {group.label}
                  </span>
                  <div className="flex-1 border-t border-white/20" />
                </div>
              )}
              <div className="space-y-1">
                {group.items.map((item) => (
                  <button
                    key={item.id}
                    data-testid={`admin-nav-${item.id}`}
                    onClick={() => setActiveTab(item.id)}
                    className={`admin-nav-item w-full flex items-center gap-3 text-left text-white/80 hover:text-white ${
                      activeTab === item.id ? 'active' : ''
                    }`}
                  >
                    <item.icon className="h-5 w-5" />
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="flex-shrink-0 p-4 border-t border-white/10">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 bg-[#9A3B26] rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-sm">{user?.name?.charAt(0)}</span>
            </div>
            <div className="min-w-0">
              <p className="text-white font-medium text-sm truncate">{user?.name}</p>
              <p className="text-white/60 text-xs">Admin</p>
            </div>
          </div>
          <Button
            data-testid="admin-logout-btn"
            variant="ghost"
            onClick={handleLogout}
            className="w-full text-white/60 hover:text-white hover:bg-white/10"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 md:ml-64 p-4 md:p-8">
        {activeTab === 'overview' && <OverviewTab onNavigateTab={setActiveTab} />}
        {activeTab === 'content' && <ContentTab />}
        {activeTab === 'curriculum' && <CurriculumTab />}
        {activeTab === 'videos' && <VideosTab />}
        {activeTab === 'sessions' && <LiveSessionsTab />}
        {activeTab === 'testimonials' && <TestimonialsTab />}
        {activeTab === 'faqs' && <FAQsTab />}
        {activeTab === 'instructors' && <InstructorsTab />}
        {activeTab === 'users' && <UsersTab />}
        {activeTab === 'progress' && <ProgressTab />}
        {activeTab === 'payments' && <PaymentsTab />}
        {activeTab === 'settings' && <SettingsTab />}
        {activeTab === 'blog' && <BlogTab />}
      </main>
    </div>
  );
}

// Overview Tab
function OverviewTab({ onNavigateTab }) {
  const [users, setUsers] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [videos, setVideos] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      adminApi.getUsers().catch(() => ({ data: [] })),
      adminApi.getRegistrations().catch(() => ({ data: [] })),
      adminApi.getVideos().catch(() => ({ data: [] })),
      adminApi.getLiveSessions().catch(() => ({ data: [] })),
    ]).then(([u, r, v, s]) => {
      setUsers(u.data || []);
      setRegistrations(r.data || []);
      setVideos(v.data || []);
      setSessions(s.data || []);
    }).finally(() => setLoading(false));
  }, []);

  const revenue = registrations.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
  const registeredUsers = users.filter(u => u.is_registered);
  const recentUsers = [...users].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 8);
  const recentRegistrations = [...registrations].sort((a, b) => new Date(b.registered_at || b.created_at) - new Date(a.registered_at || a.created_at)).slice(0, 8);
  const upcomingSessions = [...sessions]
    .filter(s => s.live_start_at && new Date(s.live_start_at) > new Date())
    .sort((a, b) => new Date(a.live_start_at) - new Date(b.live_start_at))
    .slice(0, 4);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-[#9A3B26] border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <h1 className="font-heading text-3xl font-bold text-[#292524]">Dashboard Overview</h1>

      {/* Stat cards — click to navigate */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <StatCard title="Total Users" value={users.length} icon={Users} color="bg-[#9A3B26]" onClick={() => onNavigateTab?.('users')} />
        <StatCard title="Registered" value={registeredUsers.length} icon={CreditCard} color="bg-[#2C5E4B]" onClick={() => onNavigateTab?.('payments')} />
        <StatCard title="Videos" value={videos.length} icon={Video} color="bg-[#DDA15E]" onClick={() => onNavigateTab?.('videos')} />
        <StatCard
          title="Revenue"
          value={revenue > 0 ? `₹${revenue.toLocaleString('en-IN')}` : '—'}
          icon={CreditCard}
          color="bg-[#292524]"
          onClick={() => onNavigateTab?.('payments')}
        />
      </div>

      {/* Quick actions */}
      <div className="bg-white rounded-xl border border-[#E7E5E4] p-5">
        <h2 className="font-heading text-lg font-bold text-[#292524] mb-4">Quick Actions</h2>
        <div className="grid sm:grid-cols-4 gap-3">
          <Link to="/" className="flex items-center gap-3 p-3 bg-[#F4EFE6] rounded-lg hover:bg-[#E7E5E4] transition-colors">
            <Home className="h-5 w-5 text-[#9A3B26]" />
            <span className="font-medium text-sm text-[#292524]">View Site</span>
          </Link>
          <button onClick={() => onNavigateTab?.('sessions')} className="flex items-center gap-3 p-3 bg-[#F4EFE6] rounded-lg hover:bg-[#E7E5E4] transition-colors text-left">
            <Plus className="h-5 w-5 text-[#9A3B26]" />
            <span className="font-medium text-sm text-[#292524]">Add Session</span>
          </button>
          <button onClick={() => onNavigateTab?.('videos')} className="flex items-center gap-3 p-3 bg-[#F4EFE6] rounded-lg hover:bg-[#E7E5E4] transition-colors text-left">
            <Video className="h-5 w-5 text-[#2C5E4B]" />
            <span className="font-medium text-sm text-[#292524]">Add Video</span>
          </button>
          <button onClick={() => onNavigateTab?.('content')} className="flex items-center gap-3 p-3 bg-[#F4EFE6] rounded-lg hover:bg-[#E7E5E4] transition-colors text-left">
            <FileText className="h-5 w-5 text-[#DDA15E]" />
            <span className="font-medium text-sm text-[#292524]">Edit Content</span>
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Recent Users */}
        <div className="bg-white rounded-xl border border-[#E7E5E4] overflow-hidden">
          <div className="px-5 py-4 border-b border-[#E7E5E4] flex items-center justify-between">
            <h2 className="font-heading text-lg font-bold text-[#292524]">Recent Users</h2>
            <button onClick={() => onNavigateTab?.('users')} className="text-sm text-[#9A3B26] font-medium hover:underline">
              View all →
            </button>
          </div>
          {recentUsers.length === 0 ? (
            <p className="p-5 text-sm text-[#78716C]">No users yet.</p>
          ) : (
            <div className="divide-y divide-[#F4EFE6]">
              {recentUsers.map(u => (
                <div key={u._id} className="px-5 py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium text-sm text-[#292524] truncate">{u.name || '—'}</p>
                    <p className="text-xs text-[#78716C] truncate">{u.email}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {u.role === 'admin' && (
                      <span className="text-xs bg-[#292524] text-white px-2 py-0.5 rounded-full">Admin</span>
                    )}
                    {u.is_registered ? (
                      <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">Registered</span>
                    ) : (
                      <span className="text-xs bg-[#F4EFE6] text-[#78716C] px-2 py-0.5 rounded-full">Pending</span>
                    )}
                    <span className="text-xs text-[#78716C] hidden sm:block">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '—'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Upcoming Live Sessions */}
        <div className="bg-white rounded-xl border border-[#E7E5E4] overflow-hidden">
          <div className="px-5 py-4 border-b border-[#E7E5E4] flex items-center justify-between">
            <h2 className="font-heading text-lg font-bold text-[#292524]">Upcoming Sessions</h2>
            <button onClick={() => onNavigateTab?.('sessions')} className="text-sm text-[#9A3B26] font-medium hover:underline">
              Manage →
            </button>
          </div>
          {upcomingSessions.length === 0 ? (
            <div className="p-5">
              <p className="text-sm text-[#78716C] mb-3">No upcoming sessions scheduled.</p>
              <button
                onClick={() => onNavigateTab?.('sessions')}
                className="text-sm font-medium text-[#9A3B26] hover:underline"
              >
                + Create a live session
              </button>
            </div>
          ) : (
            <div className="divide-y divide-[#F4EFE6]">
              {upcomingSessions.map((s, i) => (
                <div key={i} className="px-5 py-3">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-medium text-sm text-[#292524] truncate">{s.title}</p>
                    <span className="text-xs bg-[#F4EFE6] text-[#9A3B26] px-2 py-0.5 rounded-full shrink-0">Day {s.day ?? '—'}</span>
                  </div>
                  <p className="text-xs text-[#78716C] mt-0.5">
                    {new Date(s.live_start_at).toLocaleString('en-IN', {
                      day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata'
                    })} IST
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Recent Registrations */}
      <div className="bg-white rounded-xl border border-[#E7E5E4] overflow-hidden">
        <div className="px-5 py-4 border-b border-[#E7E5E4] flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold text-[#292524]">Recent Registrations</h2>
          <button onClick={() => onNavigateTab?.('payments')} className="text-sm text-[#9A3B26] font-medium hover:underline">
            View all →
          </button>
        </div>
        {recentRegistrations.length === 0 ? (
          <p className="p-5 text-sm text-[#78716C]">No registrations yet. Payments will appear here once users complete checkout.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F9F7F3] border-b border-[#E7E5E4]">
                <tr>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-[#78716C] uppercase tracking-wide">User</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-[#78716C] uppercase tracking-wide">Package</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-[#78716C] uppercase tracking-wide">Amount</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-[#78716C] uppercase tracking-wide">Gateway</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-[#78716C] uppercase tracking-wide">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F4EFE6]">
                {recentRegistrations.map((r, i) => (
                  <tr key={i} className="hover:bg-[#F9F7F3]">
                    <td className="px-5 py-3 text-[#292524] truncate max-w-[180px]">{r.user_email || '—'}</td>
                    <td className="px-5 py-3 text-[#78716C]">{r.package_id || '—'}</td>
                    <td className="px-5 py-3 font-medium text-[#292524]">
                      {r.amount ? `${r.currency === 'inr' ? '₹' : '$'}${Number(r.amount).toLocaleString()}` : '—'}
                    </td>
                    <td className="px-5 py-3">
                      <span className="inline-block text-xs bg-[#F4EFE6] text-[#9A3B26] px-2 py-0.5 rounded-full capitalize">
                        {r.payment_gateway || r.gateway || '—'}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-[#78716C]">
                      {r.registered_at ? new Date(r.registered_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ title, value, icon: Icon, color, onClick }) {
  return (
    <button
      onClick={onClick}
      className="bg-white rounded-xl border border-[#E7E5E4] p-6 text-left hover:border-[#9A3B26] hover:shadow-md transition-all w-full group"
    >
      <div className="flex items-center justify-between mb-4">
        <div className={`w-12 h-12 ${color} rounded-xl flex items-center justify-center`}>
          <Icon className="h-6 w-6 text-white" />
        </div>
        <span className="text-xs text-[#78716C] group-hover:text-[#9A3B26] transition-colors">View →</span>
      </div>
      <p className="text-sm text-[#78716C] mb-1">{title}</p>
      <p className="font-heading text-3xl font-bold text-[#292524]">{value}</p>
    </button>
  );
}

// Content Tab
function ContentTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingHome, setSavingHome] = useState(false);
  const [savingAbout, setSavingAbout] = useState(false);
  const [about, setAbout] = useState({
    hero_title: 'About The Kalari Movement',
    hero_subtitle: 'We exist to carry one of the world\'s oldest martial traditions into the hands of every sincere practitioner.',
    story_heading: 'How We Began',
    story_body: '',
    story_image_url: '',
    story_image_lqip: '',
    founder_name: '',
    founder_title: '',
    founder_bio: '',
    founder_image_url: '',
    founder_image_lqip: '',
    value_1_title: 'Authenticity', value_1_body: '',
    value_2_title: 'Rigour',       value_2_body: '',
    value_3_title: 'Community',    value_3_body: '',
    value_4_title: 'Accessibility',value_4_body: '',
  });
  const [hero, setHero] = useState({
    badge_text: '21-Day Transformation Programme',
    heading: 'Awaken the Warrior Within',
    highlight_word: 'Warrior',
    subheading: 'Discover the ancient martial art of Kalaripayattu. Transform your body, sharpen your mind, and unlock your inner strength through 21 days of guided practice.',
    hero_image_url: 'https://images.unsplash.com/photo-1610308235436-3e1cde290a87?w=800&q=80',
    joined_text: '2500+ people already joined',
  });
  const [homeImages, setHomeImages] = useState({
    hero_image_url: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=600&q=80',
    hero_image_lqip: '',
    about_image_url: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=600&q=80',
    about_image_lqip: '',
  });

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await adminApi.getContent();
        const heroSection = response.data.find((s) => s.section_id === 'hero');
        if (heroSection?.content) setHero((prev) => ({ ...prev, ...heroSection.content }));
        const homeSection = response.data.find((s) => s.section_id === 'homepage');
        if (homeSection?.content) setHomeImages((prev) => ({ ...prev, ...homeSection.content }));
        const aboutSection = response.data.find((s) => s.section_id === 'about');
        if (aboutSection?.content) setAbout((prev) => ({ ...prev, ...aboutSection.content }));
      } catch (error) {
        console.error('Failed to fetch content:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminApi.saveContent({ section_id: 'hero', title: hero.heading, subtitle: hero.subheading, content: hero, is_active: true });
      alert('Programme page content saved');
    } catch (error) {
      alert(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAbout = async () => {
    setSavingAbout(true);
    try {
      await adminApi.saveContent({ section_id: 'about', title: 'About Page', content: about, is_active: true });
      alert('About page content saved');
    } catch (error) {
      alert(formatApiError(error));
    } finally {
      setSavingAbout(false);
    }
  };

  const handleSaveHome = async () => {
    setSavingHome(true);
    try {
      await adminApi.saveContent({ section_id: 'homepage', title: 'Homepage', content: homeImages, is_active: true });
      alert('Homepage photos saved');
    } catch (error) {
      alert(formatApiError(error));
    } finally {
      setSavingHome(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-8 max-w-3xl">
      <h1 className="font-heading text-3xl font-bold text-[#292524]">Content Management</h1>

      {/* ── Homepage Photos ── */}
      <div className="bg-white rounded-xl border border-[#E7E5E4] p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-8 h-8 rounded-lg bg-[#F4EFE6] flex items-center justify-center">
            <Image className="h-4 w-4 text-[#9A3B26]" />
          </div>
          <div>
            <h2 className="font-heading text-lg font-bold text-[#292524]">Homepage Photos</h2>
            <p className="text-xs text-[#78716C]">Photos shown on the main brand homepage (thekalarimovement.com)</p>
          </div>
        </div>
        <div className="space-y-6">
          <ImageUploadField
            label="Hero Background Photo"
            value={homeImages.hero_image_url}
            onChange={(val) => setHomeImages(prev => ({ ...prev, hero_image_url: val }))}
            onLqipChange={(lqip) => setHomeImages(prev => ({ ...prev, hero_image_lqip: lqip }))}
            maxWidth={1920}          />
          <ImageUploadField
            label="About Section Photo"
            value={homeImages.about_image_url}
            onChange={(val) => setHomeImages(prev => ({ ...prev, about_image_url: val }))}
            onLqipChange={(lqip) => setHomeImages(prev => ({ ...prev, about_image_lqip: lqip }))}
            maxWidth={1200}          />
        </div>
        <div className="mt-6">
          <Button onClick={handleSaveHome} disabled={savingHome} className="bg-[#9A3B26] hover:bg-[#7A2E1E]">
            {savingHome ? 'Saving…' : 'Save Homepage Photos'}
          </Button>
        </div>
      </div>

      {/* ── Programme Page Hero ── */}
      <div className="bg-white rounded-xl border border-[#E7E5E4] p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-8 h-8 rounded-lg bg-[#F4EFE6] flex items-center justify-center">
            <Image className="h-4 w-4 text-[#9A3B26]" />
          </div>
          <div>
            <h2 className="font-heading text-lg font-bold text-[#292524]">Programme Page — Hero Section</h2>
            <p className="text-xs text-[#78716C]">Text and photo shown at the top of the /programme page</p>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Badge Text</Label>
            <Input value={hero.badge_text} onChange={(e) => setHero({ ...hero, badge_text: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label>Highlight Word</Label>
            <Input value={hero.highlight_word} onChange={(e) => setHero({ ...hero, highlight_word: e.target.value })} />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label>Heading</Label>
            <Input value={hero.heading} onChange={(e) => setHero({ ...hero, heading: e.target.value })} />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label>Subheading</Label>
            <Textarea rows={3} value={hero.subheading} onChange={(e) => setHero({ ...hero, subheading: e.target.value })} />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label>Joined Text</Label>
            <Input value={hero.joined_text} onChange={(e) => setHero({ ...hero, joined_text: e.target.value })} />
          </div>
          <div className="space-y-2 md:col-span-2">
            <ImageUploadField
              label="Hero Photo"
              value={hero.hero_image_url}
              onChange={(val) => setHero({ ...hero, hero_image_url: val })}
              maxWidth={1920}
            />
          </div>
        </div>
        <div className="mt-6">
          <Button onClick={handleSave} disabled={saving} className="bg-[#2C5E4B] hover:bg-[#244c3d]">
            {saving ? 'Saving…' : 'Save Programme Page Content'}
          </Button>
        </div>
      </div>

      {/* ── About Page ── */}
      <div className="bg-white rounded-xl border border-[#E7E5E4] p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-8 h-8 rounded-lg bg-[#F4EFE6] flex items-center justify-center">
            <Image className="h-4 w-4 text-[#9A3B26]" />
          </div>
          <div>
            <h2 className="font-heading text-lg font-bold text-[#292524]">About Page</h2>
            <p className="text-xs text-[#78716C]">Content shown on thekalarimovement.com/about</p>
          </div>
        </div>

        <div className="space-y-6">
          {/* Hero */}
          <div>
            <p className="text-xs font-semibold text-[#78716C] uppercase tracking-wide mb-3">Hero Section</p>
            <div className="space-y-3">
              <div className="space-y-1">
                <Label>Page Title</Label>
                <Input value={about.hero_title} onChange={e => setAbout({ ...about, hero_title: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label>Hero Subtitle</Label>
                <Textarea rows={2} value={about.hero_subtitle} onChange={e => setAbout({ ...about, hero_subtitle: e.target.value })} />
              </div>
            </div>
          </div>

          {/* Story */}
          <div>
            <p className="text-xs font-semibold text-[#78716C] uppercase tracking-wide mb-3">Our Story Section</p>
            <div className="space-y-3">
              <div className="space-y-1">
                <Label>Section Heading</Label>
                <Input value={about.story_heading} onChange={e => setAbout({ ...about, story_heading: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label>Story Text (separate paragraphs with a blank line)</Label>
                <Textarea rows={6} value={about.story_body} onChange={e => setAbout({ ...about, story_body: e.target.value })} />
              </div>
              <ImageUploadField
                label="Story Photo"
                value={about.story_image_url}
                onChange={val => setAbout(prev => ({ ...prev, story_image_url: val }))}
                onLqipChange={lqip => setAbout(prev => ({ ...prev, story_image_lqip: lqip }))}
                maxWidth={1200}
              />
            </div>
          </div>

          {/* Founder */}
          <div>
            <p className="text-xs font-semibold text-[#78716C] uppercase tracking-wide mb-3">About the Founder</p>
            <div className="space-y-3">
              <div className="space-y-1">
                <Label>Founder Name</Label>
                <Input value={about.founder_name} onChange={e => setAbout({ ...about, founder_name: e.target.value })} placeholder="e.g. Guru Pradeep" />
              </div>
              <div className="space-y-1">
                <Label>Title / Role</Label>
                <Input value={about.founder_title} onChange={e => setAbout({ ...about, founder_title: e.target.value })} placeholder="e.g. Founder & Head Instructor" />
              </div>
              <div className="space-y-1">
                <Label>Bio (separate paragraphs with a blank line)</Label>
                <Textarea rows={5} value={about.founder_bio} onChange={e => setAbout({ ...about, founder_bio: e.target.value })} placeholder="Write about the founder's background, training, and vision..." />
              </div>
              <ImageUploadField
                label="Founder Photo"
                value={about.founder_image_url}
                onChange={val => setAbout(prev => ({ ...prev, founder_image_url: val }))}
                onLqipChange={lqip => setAbout(prev => ({ ...prev, founder_image_lqip: lqip }))}
                maxWidth={1200}
              />
            </div>
          </div>

          {/* Values */}
          <div>
            <p className="text-xs font-semibold text-[#78716C] uppercase tracking-wide mb-3">Our Values (4 cards)</p>
            <div className="grid md:grid-cols-2 gap-4">
              {[1, 2, 3, 4].map(n => (
                <div key={n} className="border border-[#E7E5E4] rounded-lg p-4 space-y-2">
                  <div className="space-y-1">
                    <Label className="text-xs">Value {n} Title</Label>
                    <Input value={about[`value_${n}_title`]} onChange={e => setAbout({ ...about, [`value_${n}_title`]: e.target.value })} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Value {n} Description</Label>
                    <Textarea rows={2} value={about[`value_${n}_body`]} onChange={e => setAbout({ ...about, [`value_${n}_body`]: e.target.value })} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-6">
          <Button onClick={handleSaveAbout} disabled={savingAbout} className="bg-[#9A3B26] hover:bg-[#7A2E1E]">
            {savingAbout ? 'Saving…' : 'Save About Page Content'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// Curriculum Tab
const EMPTY_DAY = { day: '', week: 1, title: '', knowledge: '', practice: '', relaxation: '', special: '' };

function CurriculumTab() {
  const [curriculum, setCurriculum] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingDay, setEditingDay] = useState(null); // null | 'new' | day number
  const [formData, setFormData] = useState(EMPTY_DAY);

  useEffect(() => { fetchCurriculum(); }, []);

  const fetchCurriculum = async () => {
    try {
      const response = await adminApi.getCurriculum();
      setCurriculum(response.data);
    } catch (error) {
      console.error('Failed to fetch curriculum:', error);
    } finally {
      setLoading(false);
    }
  };

  const openEdit = (day) => {
    setFormData({ day: day.day, week: day.week, title: day.title || '', knowledge: day.knowledge || '', practice: day.practice || '', relaxation: day.relaxation || '', special: day.special || '' });
    setEditingDay(day.day);
  };
  const openNew = () => { setFormData(EMPTY_DAY); setEditingDay('new'); };
  const closeForm = () => { setEditingDay(null); setFormData(EMPTY_DAY); };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.day || !formData.title) return;
    setSaving(true);
    try {
      await adminApi.saveCurriculum({ ...formData, day: parseInt(formData.day), week: parseInt(formData.week) });
      await fetchCurriculum();
      closeForm();
    } catch (error) {
      alert('Failed to save day');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (dayNum) => {
    if (!window.confirm(`Delete Day ${dayNum} from the curriculum? This cannot be undone.`)) return;
    try {
      await adminApi.deleteCurriculumDay(dayNum);
      if (editingDay === dayNum) closeForm();
      await fetchCurriculum();
    } catch (error) {
      alert('Failed to delete day');
    }
  };

  // Group days by week
  const byWeek = curriculum.reduce((acc, d) => {
    const w = d.week || 1;
    if (!acc[w]) acc[w] = [];
    acc[w].push(d);
    return acc;
  }, {});
  const weekNums = Object.keys(byWeek).map(Number).sort((a, b) => a - b);

  const handleDeleteWeek = async (weekNum) => {
    const days = byWeek[weekNum] || [];
    if (!window.confirm(`Delete all ${days.length} day(s) in Week ${weekNum}? This cannot be undone.`)) return;
    try {
      await adminApi.deleteCurriculumWeek(weekNum);
      if (days.some(d => d.day === editingDay)) closeForm();
      await fetchCurriculum();
    } catch (error) {
      alert('Failed to delete week');
    }
  };

  const field = (label, key, opts = {}) => (
    <div className="space-y-1">
      <Label className="text-xs font-semibold text-[#78716C] uppercase tracking-wide">{label}</Label>
      {opts.textarea ? (
        <Textarea value={formData[key]} onChange={(e) => setFormData({ ...formData, [key]: e.target.value })} rows={2} placeholder={opts.placeholder || ''} className="text-sm" />
      ) : (
        <Input value={formData[key]} onChange={(e) => setFormData({ ...formData, [key]: e.target.value })} placeholder={opts.placeholder || ''} type={opts.type || 'text'} min={opts.min} max={opts.max} required={!!opts.required} className="text-sm" />
      )}
    </div>
  );

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-heading text-3xl font-bold text-[#292524]">Curriculum Management</h1>
        {editingDay === null && (
          <Button onClick={openNew} className="bg-[#9A3B26] hover:bg-[#7A2E1E]">
            <Plus className="h-4 w-4 mr-2" /> Add Day
          </Button>
        )}
      </div>

      {/* Edit / Add form */}
      {editingDay !== null && (
        <div className="bg-white rounded-xl border border-[#E7E5E4] p-6 mb-8">
          <h2 className="font-heading text-lg font-semibold text-[#292524] mb-5">
            {editingDay === 'new' ? 'Add New Day' : `Edit Day ${editingDay}`}
          </h2>
          <form onSubmit={handleSave}>
            <div className="grid md:grid-cols-4 gap-4 mb-4">
              <div className="space-y-1">
                <Label className="text-xs font-semibold text-[#78716C] uppercase tracking-wide">Day Number <span className="text-red-500">*</span></Label>
                <Input
                  type="number" min="1" max="100" required
                  value={formData.day}
                  onChange={(e) => setFormData({ ...formData, day: e.target.value })}
                  disabled={editingDay !== 'new'}
                  placeholder="e.g. 1"
                  className="text-sm"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-semibold text-[#78716C] uppercase tracking-wide">Week <span className="text-red-500">*</span></Label>
                <select
                  value={formData.week}
                  onChange={(e) => setFormData({ ...formData, week: parseInt(e.target.value) })}
                  className="w-full rounded-lg border border-[#E7E5E4] px-3 py-2 text-sm bg-white text-[#292524]"
                >
                  {[1, 2, 3].map(w => <option key={w} value={w}>Week {w}</option>)}
                </select>
              </div>
              <div className="md:col-span-2 space-y-1">
                <Label className="text-xs font-semibold text-[#78716C] uppercase tracking-wide">Title <span className="text-red-500">*</span></Label>
                <Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required placeholder="e.g. Foundation Stances" className="text-sm" />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              {field('Knowledge (what students learn)', 'knowledge', { textarea: true, placeholder: 'e.g. History of Kalaripayattu, body mechanics...' })}
              {field('Practice (what students do)', 'practice', { textarea: true, placeholder: 'e.g. Warm-up exercises, basic stance...' })}
              {field('Relaxation / Cool-down', 'relaxation', { textarea: true, placeholder: 'e.g. Breathing exercises, stretches...' })}
              {field('Special / Notes', 'special', { textarea: true, placeholder: 'e.g. Guest instructor, outdoor session...' })}
            </div>
            <div className="flex gap-3">
              <Button type="submit" disabled={saving} className="bg-[#2C5E4B] hover:bg-[#244c3d]">
                {saving ? 'Saving…' : editingDay === 'new' ? 'Add Day' : 'Save Changes'}
              </Button>
              <Button type="button" variant="outline" onClick={closeForm}>Cancel</Button>
            </div>
          </form>
        </div>
      )}

      {/* Week-grouped curriculum list */}
      {weekNums.map((weekNum) => (
        <div key={weekNum} className="mb-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-px flex-1 bg-[#E7E5E4]" />
            <span className="text-xs font-bold text-[#9A3B26] uppercase tracking-widest px-3 py-1 bg-[#FDF6EF] rounded-full border border-[#E7CDB8]">
              Week {weekNum}
            </span>
            <div className="h-px flex-1 bg-[#E7E5E4]" />
            <button
              onClick={() => handleDeleteWeek(weekNum)}
              title={`Delete all days in Week ${weekNum}`}
              className="text-red-400 hover:text-red-600 p-1 rounded transition-colors"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
          <div className="bg-white rounded-xl border border-[#E7E5E4] overflow-hidden">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th className="hidden md:table-cell">Knowledge</th>
                  <th className="hidden md:table-cell">Practice</th>
                  <th className="w-24">Actions</th>
                </tr>
              </thead>
              <tbody>
                {byWeek[weekNum].map((day) => (
                  <tr key={day.day} className={editingDay === day.day ? 'bg-[#FDF6EF]' : ''}>
                    <td className="font-medium">{day.title}</td>
                    <td className="hidden md:table-cell text-sm text-[#78716C] max-w-[180px] truncate">{day.knowledge || <span className="text-[#C8BFB5]">—</span>}</td>
                    <td className="hidden md:table-cell text-sm text-[#78716C] max-w-[180px] truncate">{day.practice || <span className="text-[#C8BFB5]">—</span>}</td>
                    <td>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(day)} className="text-[#78716C] hover:text-[#292524]">
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(day.day)} className="text-red-400 hover:text-red-600">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {curriculum.length === 0 && editingDay === null && (
        <div className="text-center py-16 text-[#78716C]">
          <Calendar className="h-12 w-12 mx-auto mb-3 text-[#C8BFB5]" />
          <p className="font-medium">No curriculum days yet</p>
          <p className="text-sm mt-1">Add your first day above</p>
        </div>
      )}
    </div>
  );
}

// Videos Tab
function VideosTab() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    video_type: 'youtube',
    video_url: '',
    thumbnail_url: '',
    day: null,
    is_active: true
  });

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      const response = await adminApi.getVideos();
      setVideos(response.data);
    } catch (error) {
      console.error('Failed to fetch videos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await adminApi.createVideo(formData);
      await fetchVideos();
      setShowForm(false);
      setFormData({ title: '', description: '', video_type: 'youtube', video_url: '', thumbnail_url: '', day: null, is_active: true });
    } catch (error) {
      console.error('Failed to create video:', error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this video?')) {
      try {
        await adminApi.deleteVideo(id);
        await fetchVideos();
      } catch (error) {
        console.error('Failed to delete video:', error);
      }
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-heading text-3xl font-bold text-[#292524]">Video Management</h1>
        <Button 
          data-testid="add-video-btn"
          onClick={() => setShowForm(!showForm)} 
          className="bg-[#9A3B26] hover:bg-[#7A2E1E]"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Video
        </Button>
      </div>

      {showForm && (
        <div className="bg-white rounded-xl border border-[#E7E5E4] p-6 mb-8">
          <h2 className="font-heading text-xl font-bold text-[#292524] mb-4">Add New Video</h2>
          <form onSubmit={handleSubmit} className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Video Type</Label>
              <select
                value={formData.video_type}
                onChange={(e) => setFormData({ ...formData, video_type: e.target.value })}
                className="w-full rounded-lg border border-[#E7E5E4] p-2"
              >
                <option value="youtube">YouTube</option>
                <option value="self_hosted">Self Hosted</option>
                <option value="zoom_recording">Zoom Recording</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>Video URL</Label>
              <Input
                value={formData.video_url}
                onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                placeholder="https://youtube.com/watch?v=..."
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Day (Optional)</Label>
              <Input
                type="number"
                min="1"
                max="21"
                value={formData.day || ''}
                onChange={(e) => setFormData({ ...formData, day: e.target.value ? parseInt(e.target.value) : null })}
                placeholder="1-21"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
              />
            </div>
            <div className="md:col-span-2 flex gap-4">
              <Button type="submit" className="bg-[#2C5E4B]">Save Video</Button>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white rounded-xl border border-[#E7E5E4] overflow-hidden">
        {videos.length === 0 ? (
          <div className="p-12 text-center text-[#78716C]">
            No videos yet. Add your first video above.
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Type</th>
                <th>Day</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {videos.map((video) => (
                <tr key={video._id}>
                  <td className="font-medium">{video.title}</td>
                  <td>
                    <span className="px-2 py-1 bg-[#F4EFE6] rounded text-sm">
                      {video.video_type}
                    </span>
                  </td>
                  <td>{video.day || '-'}</td>
                  <td>
                    <span className={`px-2 py-1 rounded text-sm ${video.is_active ? 'badge-success' : 'badge-error'}`}>
                      {video.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDelete(video._id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// Testimonials Tab
function TestimonialsTab() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null); // null = closed, 'new' = add, id = edit
  const emptyForm = { name: '', age: '', location: '', quote: '', rating: 5, is_featured: false, is_active: true };
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => { fetchTestimonials(); }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await adminApi.getTestimonials();
      setTestimonials(response.data);
    } catch (error) {
      console.error('Failed to fetch testimonials:', error);
    } finally {
      setLoading(false);
    }
  };

  const openNew = () => { setFormData(emptyForm); setEditingId('new'); };
  const openEdit = (t) => {
    setFormData({ name: t.name || '', age: t.age || '', location: t.location || '', quote: t.quote || '', rating: t.rating || 5, is_featured: !!t.is_featured, is_active: t.is_active !== false });
    setEditingId(t._id);
  };
  const closeForm = () => { setEditingId(null); setFormData(emptyForm); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...formData, age: formData.age ? parseInt(formData.age) : null };
    try {
      if (editingId === 'new') {
        await adminApi.createTestimonial(payload);
      } else {
        await adminApi.updateTestimonial(editingId, payload);
      }
      await fetchTestimonials();
      closeForm();
    } catch (error) {
      alert('Failed to save testimonial');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this testimonial?')) return;
    try {
      await adminApi.deleteTestimonial(id);
      if (editingId === id) closeForm();
      await fetchTestimonials();
    } catch (error) {
      console.error('Failed to delete:', error);
    }
  };

  const starLabel = (n) => '★'.repeat(n) + '☆'.repeat(5 - n);

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-heading text-3xl font-bold text-[#292524]">Testimonials</h1>
        {editingId === null && (
          <Button onClick={openNew} className="bg-[#9A3B26] hover:bg-[#7A2E1E]">
            <Plus className="h-4 w-4 mr-2" /> Add Testimonial
          </Button>
        )}
      </div>

      {/* Add / Edit form */}
      {editingId !== null && (
        <div className="bg-white rounded-xl border border-[#E7E5E4] p-6 mb-8">
          <h2 className="font-heading text-lg font-semibold text-[#292524] mb-5">
            {editingId === 'new' ? 'Add Testimonial' : 'Edit Testimonial'}
          </h2>
          <form onSubmit={handleSubmit} className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Name <span className="text-red-500">*</span></Label>
              <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required placeholder="e.g. Priya Nair" />
            </div>
            <div className="space-y-2">
              <Label>Age</Label>
              <Input type="number" min="1" max="120" value={formData.age} onChange={(e) => setFormData({ ...formData, age: e.target.value })} placeholder="e.g. 34" />
            </div>
            <div className="space-y-2">
              <Label>Location</Label>
              <Input value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} placeholder="e.g. Kochi, Kerala" />
            </div>
            <div className="space-y-2">
              <Label>Rating</Label>
              <select
                value={formData.rating}
                onChange={(e) => setFormData({ ...formData, rating: parseInt(e.target.value) })}
                className="w-full rounded-lg border border-[#E7E5E4] px-3 py-2 text-sm bg-white text-[#292524]"
              >
                {[5, 4, 3, 2, 1].map(n => (
                  <option key={n} value={n}>{starLabel(n)} ({n}/5)</option>
                ))}
              </select>
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Quote <span className="text-red-500">*</span></Label>
              <Textarea
                value={formData.quote}
                onChange={(e) => setFormData({ ...formData, quote: e.target.value })}
                rows={4} required
                placeholder="What did this person say about the programme?"
              />
              <p className="text-xs text-[#A8A29E]">{formData.quote.length} characters</p>
            </div>
            <div className="md:col-span-2 flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={formData.is_featured} onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })} className="rounded" />
                <span className="text-sm font-medium text-[#292524]">Featured on landing page</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={formData.is_active} onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} className="rounded" />
                <span className="text-sm font-medium text-[#292524]">Active (visible)</span>
              </label>
            </div>
            <div className="md:col-span-2 flex gap-3 pt-1">
              <Button type="submit" disabled={saving} className="bg-[#2C5E4B] hover:bg-[#244c3d]">
                {saving ? 'Saving…' : editingId === 'new' ? 'Add Testimonial' : 'Save Changes'}
              </Button>
              <Button type="button" variant="outline" onClick={closeForm}>Cancel</Button>
            </div>
          </form>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {testimonials.map((t) => (
          <div key={t._id} className={`bg-white rounded-xl border p-4 transition-all ${editingId === t._id ? 'border-[#9A3B26] ring-2 ring-[#9A3B26]/20' : 'border-[#E7E5E4]'}`}>
            <div className="flex justify-between items-start mb-2">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-bold text-[#292524]">{t.name}</h3>
                  {t.is_featured && (
                    <span className="px-2 py-0.5 bg-[#DDA15E] text-white text-xs rounded-full font-medium">Featured</span>
                  )}
                  {!t.is_active && (
                    <span className="px-2 py-0.5 bg-[#F4EFE6] text-[#78716C] text-xs rounded-full">Hidden</span>
                  )}
                </div>
                <p className="text-xs text-[#78716C] mt-0.5">
                  {[t.age && `${t.age} yrs`, t.location].filter(Boolean).join(' · ')}
                </p>
                <p className="text-[#DDA15E] text-sm mt-0.5">{'★'.repeat(t.rating || 5)}</p>
              </div>
              <div className="flex gap-1 shrink-0">
                <Button size="sm" variant="ghost" onClick={() => openEdit(t)} className="text-[#78716C] hover:text-[#292524]">
                  <Edit className="h-4 w-4" />
                </Button>
                <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-600" onClick={() => handleDelete(t._id)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <p className="text-[#78716C] text-sm line-clamp-3 italic">"{t.quote}"</p>
          </div>
        ))}
      </div>

      {testimonials.length === 0 && editingId === null && (
        <div className="text-center py-16 text-[#78716C]">
          <MessageSquare className="h-12 w-12 mx-auto mb-3 text-[#C8BFB5]" />
          <p className="font-medium">No testimonials yet</p>
          <p className="text-sm mt-1">Add your first testimonial above</p>
        </div>
      )}
    </div>
  );
}

// FAQs Tab
function FAQsTab() {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ question: '', answer: '', order: 0, is_active: true });

  useEffect(() => {
    fetchFaqs();
  }, []);

  const fetchFaqs = async () => {
    try {
      const response = await adminApi.getFaqs();
      setFaqs(response.data);
    } catch (error) {
      console.error('Failed to fetch FAQs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await adminApi.createFaq(formData);
      await fetchFaqs();
      setShowForm(false);
      setFormData({ question: '', answer: '', order: 0, is_active: true });
    } catch (error) {
      console.error('Failed to create FAQ:', error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this FAQ?')) {
      try {
        await adminApi.deleteFaq(id);
        await fetchFaqs();
      } catch (error) {
        console.error('Failed to delete:', error);
      }
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-heading text-3xl font-bold text-[#292524]">FAQs</h1>
        <Button onClick={() => setShowForm(!showForm)} className="bg-[#9A3B26] hover:bg-[#7A2E1E]">
          <Plus className="h-4 w-4 mr-2" />
          Add FAQ
        </Button>
      </div>

      {showForm && (
        <div className="bg-white rounded-xl border border-[#E7E5E4] p-6 mb-8">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Question</Label>
              <Input value={formData.question} onChange={(e) => setFormData({ ...formData, question: e.target.value })} required />
            </div>
            <div className="space-y-2">
              <Label>Answer</Label>
              <Textarea value={formData.answer} onChange={(e) => setFormData({ ...formData, answer: e.target.value })} rows={4} required />
            </div>
            <div className="space-y-2">
              <Label>Order</Label>
              <Input type="number" value={formData.order} onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })} />
            </div>
            <div className="flex gap-4">
              <Button type="submit" className="bg-[#2C5E4B]">Save</Button>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            </div>
          </form>
        </div>
      )}

      <div className="space-y-4">
        {faqs.map((faq) => (
          <div key={faq._id} className="bg-white rounded-xl border border-[#E7E5E4] p-4">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-bold text-[#292524] mb-2">{faq.question}</h3>
                <p className="text-[#78716C] text-sm">{faq.answer}</p>
              </div>
              <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDelete(faq._id)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Instructors Tab
function InstructorsTab() {
  const [instructors, setInstructors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null); // null = closed, 'new' = add form, id = edit form
  const emptyForm = { name: '', title: '', bio: '', image_url: '', order: 0, is_active: true };
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchInstructors(); }, []);

  const fetchInstructors = async () => {
    try {
      const response = await adminApi.getInstructors();
      setInstructors(response.data);
    } catch (error) {
      console.error('Failed to fetch instructors:', error);
    } finally {
      setLoading(false);
    }
  };

  const openNew = () => { setFormData(emptyForm); setEditingId('new'); };
  const openEdit = (instructor) => {
    setFormData({ name: instructor.name || '', title: instructor.title || '', bio: instructor.bio || '', image_url: instructor.image_url || '', order: instructor.order || 0, is_active: instructor.is_active !== false });
    setEditingId(instructor._id);
  };
  const closeForm = () => { setEditingId(null); setFormData(emptyForm); };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId === 'new') {
        await adminApi.createInstructor(formData);
      } else {
        await adminApi.updateInstructor(editingId, formData);
      }
      await fetchInstructors();
      closeForm();
    } catch (error) {
      alert('Failed to save instructor');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this instructor?')) return;
    try {
      await adminApi.deleteInstructor(id);
      await fetchInstructors();
    } catch (error) {
      console.error('Failed to delete:', error);
    }
  };

  if (loading) return <LoadingSpinner />;

  const InstructorForm = () => (
    <div className="bg-white rounded-xl border border-[#E7E5E4] p-6 mb-8">
      <h2 className="font-heading text-lg font-semibold text-[#292524] mb-5">
        {editingId === 'new' ? 'Add Instructor' : 'Edit Instructor'}
      </h2>
      <form onSubmit={handleSubmit} className="space-y-5">
        <ImageUploadField
          label="Photo"
          value={formData.image_url}
          onChange={(val) => setFormData(prev => ({ ...prev, image_url: val }))}
          maxWidth={800}
        />

        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Full Name <span className="text-red-500">*</span></Label>
            <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required placeholder="e.g. Guru Pradeep" />
          </div>
          <div className="space-y-2">
            <Label>Title / Role</Label>
            <Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} placeholder="e.g. Master Instructor · 20 years experience" />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label>Bio <span className="text-red-500">*</span></Label>
            <Textarea value={formData.bio} onChange={(e) => setFormData({ ...formData, bio: e.target.value })} rows={4} required placeholder="Write a compelling bio about the instructor's background and expertise..." />
          </div>
          <div className="space-y-2">
            <Label>Display Order</Label>
            <Input type="number" min="0" value={formData.order} onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })} />
          </div>
        </div>

        <div className="flex gap-3 pt-2">
          <Button type="submit" disabled={saving} className="bg-[#2C5E4B] hover:bg-[#244c3d]">
            {saving ? 'Saving…' : 'Save Instructor'}
          </Button>
          <Button type="button" variant="outline" onClick={closeForm}>Cancel</Button>
        </div>
      </form>
    </div>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-heading text-3xl font-bold text-[#292524]">Instructors</h1>
        {editingId === null && (
          <Button onClick={openNew} className="bg-[#9A3B26] hover:bg-[#7A2E1E]">
            <Plus className="h-4 w-4 mr-2" /> Add Instructor
          </Button>
        )}
      </div>

      {editingId !== null && <InstructorForm />}

      <div className="grid md:grid-cols-2 gap-4">
        {instructors.map((instructor) => (
          <div key={instructor._id} className="bg-white rounded-xl border border-[#E7E5E4] overflow-hidden">
            <div className="flex gap-4 p-4">
              {/* Photo */}
              <div className="w-20 h-20 rounded-xl bg-[#9A3B26] flex items-center justify-center flex-shrink-0 overflow-hidden">
                {instructor.image_url ? (
                  <img src={instructor.image_url} alt={instructor.name} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-2xl text-white font-bold">{instructor.name?.charAt(0)}</span>
                )}
              </div>
              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="font-bold text-[#292524]">{instructor.name}</h3>
                    {instructor.title && <p className="text-sm text-[#9A3B26] font-medium">{instructor.title}</p>}
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(instructor)} className="text-[#78716C] hover:text-[#292524]">
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-600" onClick={() => handleDelete(instructor._id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <p className="text-[#78716C] text-sm line-clamp-2 mt-1">{instructor.bio}</p>
              </div>
            </div>
            {!instructor.image_url && (
              <div className="px-4 pb-3">
                <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5">
                  No photo uploaded — click the edit button to add one
                </p>
              </div>
            )}
          </div>
        ))}
      </div>

      {instructors.length === 0 && (
        <div className="text-center py-16 text-[#78716C]">
          <UserCircle className="h-12 w-12 mx-auto mb-3 text-[#C8BFB5]" />
          <p className="font-medium">No instructors yet</p>
          <p className="text-sm mt-1">Add your first instructor above</p>
        </div>
      )}
    </div>
  );
}

// Users Tab
function UsersTab() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [disablingId, setDisablingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [sending, setSending] = useState(false);
  const [broadcastResult, setBroadcastResult] = useState(null);
  const [linkDialog, setLinkDialog] = useState({ open: false, url: '', text: '' });
  const emailBodyRef = useRef(null);

  const insertEmailFormat = (open, close) => {
    const el = emailBodyRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const selected = emailBody.substring(start, end);
    const next = emailBody.substring(0, start) + open + selected + close + emailBody.substring(end);
    setEmailBody(next);
    setTimeout(() => { el.focus(); el.setSelectionRange(start + open.length, start + open.length + selected.length); }, 0);
  };

  const insertEmailLink = () => {
    const { url, text } = linkDialog;
    if (!url) return;
    const el = emailBodyRef.current;
    const pos = el ? el.selectionStart : emailBody.length;
    const linkHtml = `<a href="${url}" style="color:#9A3B26;">${text || url}</a>`;
    setEmailBody(prev => prev.substring(0, pos) + linkHtml + prev.substring(pos));
    setLinkDialog({ open: false, url: '', text: '' });
    setTimeout(() => { el?.focus(); }, 0);
  };
  const [bulkText, setBulkText] = useState('');
  const [inviting, setInviting] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);

  const fetchUsers = async () => {
    try {
      const response = await adminApi.getUsers();
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleSeedTestUser = async () => {
    setSeeding(true);
    try {
      await adminApi.seedTestUser();
      await fetchUsers();
      alert('Test user ensured successfully');
    } catch (error) {
      console.error('Failed to seed test user:', error);
      alert('Failed to seed test user');
    } finally {
      setSeeding(false);
    }
  };

  const handleToggleRegistration = async (userId, currentStatus, role) => {
    if (role === 'admin') return;
    setTogglingId(userId);
    try {
      await adminApi.toggleRegistration(userId);
      setUsers(prev => prev.map(u => u._id === userId ? { ...u, is_registered: !currentStatus } : u));
    } catch (error) {
      alert('Failed to update registration status');
    } finally {
      setTogglingId(null);
    }
  };

  const handleToggleDisable = async (userId, currentDisabled) => {
    setDisablingId(userId);
    try {
      await adminApi.toggleDisable(userId);
      setUsers(prev => prev.map(u => u._id === userId ? { ...u, is_disabled: !currentDisabled } : u));
    } catch {
      alert('Failed to update user status');
    } finally {
      setDisablingId(null);
    }
  };

  const handleDeleteUser = async (userId, name) => {
    if (!window.confirm(`Permanently delete "${name}"? This cannot be undone.`)) return;
    setDeletingId(userId);
    try {
      await adminApi.deleteUser(userId);
      setUsers(prev => prev.filter(u => u._id !== userId));
    } catch {
      alert('Failed to delete user');
    } finally {
      setDeletingId(null);
    }
  };

  const handleBulkInvite = async () => {
    const lines = bulkText.split('\n').map(l => l.trim()).filter(Boolean);
    const users = [];
    const bad = [];
    for (const line of lines) {
      // Accept "Name, Email" or "Name Email" or just "Email"
      const parts = line.split(/,|\t/).map(p => p.trim());
      if (parts.length >= 2) {
        users.push({ name: parts[0], email: parts[parts.length - 1] });
      } else if (parts[0].includes('@')) {
        users.push({ name: parts[0].split('@')[0], email: parts[0] });
      } else {
        bad.push(line);
      }
    }
    if (users.length === 0) { alert('No valid entries found. Use format: Name, email@example.com'); return; }
    if (bad.length > 0) { alert(`Skipping ${bad.length} unrecognised line(s):\n${bad.join('\n')}`); }
    if (!window.confirm(`Create accounts and send invitation emails to ${users.length} people?`)) return;
    setInviting(true);
    setInviteResult(null);
    try {
      const res = await adminApi.bulkInvite(users);
      setInviteResult(res.data);
      setBulkText('');
      await fetchUsers();
    } catch (err) {
      alert('Invite failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setInviting(false);
    }
  };

  const handleEmailBroadcast = async () => {
    if (!emailSubject.trim() || !emailBody.trim()) {
      alert('Please fill in both subject and message');
      return;
    }
    if (!window.confirm(`Send this email to all registered users?`)) return;
    setSending(true);
    setBroadcastResult(null);
    try {
      const res = await adminApi.emailBroadcast(emailSubject, emailBody);
      setBroadcastResult(res.data);
      setEmailSubject('');
      setEmailBody('');
    } catch (error) {
      alert('Failed to send broadcast: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSending(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  const registeredUsers = users.filter(u => u.role === 'user' && u.is_registered);

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-heading text-3xl font-bold text-[#292524]">Users</h1>
        <Button onClick={handleSeedTestUser} disabled={seeding} className="bg-[#2C5E4B] hover:bg-[#244c3d]">
          {seeding ? 'Ensuring...' : 'Ensure Test User'}
        </Button>
      </div>

      {/* Bulk Invite */}
      <div className="bg-white rounded-xl border border-[#E7E5E4] p-6 mb-6">
        <h2 className="font-heading text-lg font-semibold text-[#292524] mb-1">Invite Users in Bulk</h2>
        <p className="text-sm text-[#78716C] mb-4">
          Paste one user per line as <code className="bg-[#F4EFE6] px-1 rounded">Name, email@example.com</code>.
          Accounts are created and each person receives a personal invitation email with a link to set their own password.
        </p>
        <Textarea
          placeholder={"Arjun Menon, arjun@example.com\nPriya Nair, priya@example.com\nRahul Sharma, rahul@example.com"}
          value={bulkText}
          onChange={e => setBulkText(e.target.value)}
          rows={8}
          className="resize-none font-mono text-sm mb-3"
        />
        <div className="flex items-center gap-4 flex-wrap">
          <Button
            onClick={handleBulkInvite}
            disabled={inviting || !bulkText.trim()}
            className="bg-[#8B3A2A] hover:bg-[#7d2f1e]"
          >
            {inviting ? 'Sending invites...' : 'Create accounts & send invitations'}
          </Button>
          {inviteResult && (
            <div className="text-sm space-y-0.5">
              <p className="text-[#2C5E4B] font-medium">
                {inviteResult.emails_sent} invitation{inviteResult.emails_sent !== 1 ? 's' : ''} sent
                &nbsp;·&nbsp; {inviteResult.created} new account{inviteResult.created !== 1 ? 's' : ''} created
                {inviteResult.already_existed > 0 && ` · ${inviteResult.already_existed} already existed`}
                {inviteResult.emails_failed > 0 && (
                  <span className="text-red-600"> · {inviteResult.emails_failed} failed</span>
                )}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Email Broadcast */}
      <div className="bg-white rounded-xl border border-[#E7E5E4] p-6 mb-8">
        <h2 className="font-heading text-lg font-semibold text-[#292524] mb-1">Email All Registered Users</h2>
        <p className="text-sm text-[#78716C] mb-4">{registeredUsers.length} registered user{registeredUsers.length !== 1 ? 's' : ''} will receive this email.</p>
        <div className="space-y-3">
          <Input
            placeholder="Subject"
            value={emailSubject}
            onChange={e => setEmailSubject(e.target.value)}
            className="font-medium"
          />
          <div className="border border-[#E7E5E4] rounded-lg overflow-hidden">
            {/* Formatting toolbar */}
            <div className="flex items-center gap-1 px-3 py-2 bg-[#F9F7F4] border-b border-[#E7E5E4]">
              <button type="button" onClick={() => insertEmailFormat('<strong>', '</strong>')}
                className="px-2 py-1 text-sm font-bold text-[#292524] hover:bg-[#E7E5E4] rounded transition-colors" title="Bold">B</button>
              <button type="button" onClick={() => insertEmailFormat('<em>', '</em>')}
                className="px-2 py-1 text-sm italic text-[#292524] hover:bg-[#E7E5E4] rounded transition-colors" title="Italic">I</button>
              <div className="w-px h-4 bg-[#E7E5E4] mx-1" />
              <button type="button" onClick={() => setLinkDialog(d => ({ ...d, open: !d.open }))}
                className={`px-2 py-1 text-sm text-[#292524] hover:bg-[#E7E5E4] rounded transition-colors flex items-center gap-1 ${linkDialog.open ? 'bg-[#E7E5E4]' : ''}`} title="Insert link">
                🔗 Link
              </button>
            </div>
            {/* Link dialog */}
            {linkDialog.open && (
              <div className="flex items-center gap-2 px-3 py-2 bg-[#FFF8F0] border-b border-[#E7E5E4] flex-wrap">
                <input
                  autoFocus
                  placeholder="URL — e.g. https://thekalarimovement.com/blog/..."
                  value={linkDialog.url}
                  onChange={e => setLinkDialog(d => ({ ...d, url: e.target.value }))}
                  className="flex-1 min-w-[200px] text-sm border border-[#E7E5E4] rounded px-2 py-1 outline-none focus:border-[#9A3B26]"
                />
                <input
                  placeholder="Link text (optional)"
                  value={linkDialog.text}
                  onChange={e => setLinkDialog(d => ({ ...d, text: e.target.value }))}
                  className="w-40 text-sm border border-[#E7E5E4] rounded px-2 py-1 outline-none focus:border-[#9A3B26]"
                />
                <button type="button" onClick={insertEmailLink}
                  className="px-3 py-1 text-sm bg-[#9A3B26] text-white rounded hover:bg-[#7d2f1e] transition-colors">Insert</button>
                <button type="button" onClick={() => setLinkDialog({ open: false, url: '', text: '' })}
                  className="px-3 py-1 text-sm text-[#78716C] hover:text-[#292524] transition-colors">Cancel</button>
              </div>
            )}
            <Textarea
              ref={emailBodyRef}
              placeholder="Write your message here... Select text then click B or I to format. Use 🔗 Link to insert a clickable link."
              value={emailBody}
              onChange={e => setEmailBody(e.target.value)}
              rows={8}
              className="resize-none border-0 rounded-none focus:ring-0 font-mono text-sm"
            />
          </div>
          <p className="text-xs text-[#78716C]">Tip: select a word then click <strong>B</strong> or <strong>I</strong> to format it. Use 🔗 Link to add a clickable URL.</p>
          <div className="flex items-center gap-4">
            <Button
              onClick={handleEmailBroadcast}
              disabled={sending || registeredUsers.length === 0}
              className="bg-[#9A3B26] hover:bg-[#7d2f1e]"
            >
              {sending ? 'Sending...' : `Send to ${registeredUsers.length} user${registeredUsers.length !== 1 ? 's' : ''}`}
            </Button>
            {broadcastResult && (
              <p className="text-sm text-[#2C5E4B] font-medium">
                Sent {broadcastResult.sent} / {broadcastResult.total} — {broadcastResult.failed} failed
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-xl border border-[#E7E5E4] overflow-hidden">
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Access</th>
              <th>Joined</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u._id}>
                <td className="font-medium">{u.name}</td>
                <td className="text-[#78716C]">{u.email}</td>
                <td>
                  <span className={`px-2 py-1 rounded text-sm ${u.role === 'admin' ? 'bg-[#9A3B26] text-white' : 'bg-[#F4EFE6]'}`}>
                    {u.role}
                  </span>
                </td>
                <td>
                  {u.role === 'admin' ? (
                    <span className="px-2 py-1 rounded text-sm bg-[#9A3B26] text-white">Admin</span>
                  ) : (
                    <button
                      onClick={() => handleToggleRegistration(u._id, u.is_registered, u.role)}
                      disabled={togglingId === u._id}
                      className={`px-2 py-1 rounded text-sm cursor-pointer border-0 transition-colors ${
                        u.is_registered
                          ? 'badge-success hover:bg-red-100 hover:text-red-700'
                          : 'bg-[#F4EFE6] hover:bg-[#2C5E4B] hover:text-white'
                      }`}
                      title={u.is_registered ? 'Click to revoke access' : 'Click to grant access'}
                    >
                      {togglingId === u._id ? '...' : u.is_registered ? 'Active' : 'No access'}
                    </button>
                  )}
                </td>
                <td className="text-sm text-[#78716C]">
                  {u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}
                </td>
                <td>
                  <div className="flex flex-col gap-1">
                    {u.last_login_at ? (
                      <span className="px-2 py-1 rounded text-xs bg-green-100 text-green-700">
                        Last seen {new Date(u.last_login_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                      </span>
                    ) : u.has_password ? (
                      <span className="px-2 py-1 rounded text-xs bg-blue-50 text-blue-600">
                        Password set
                      </span>
                    ) : (
                      <span className="px-2 py-1 rounded text-xs bg-amber-50 text-amber-600">
                        Not set up
                      </span>
                    )}
                    {u.unique_ip_count > 1 && (
                      <span className="px-2 py-1 rounded text-xs bg-red-100 text-red-700" title={`Logged in from ${u.unique_ip_count} different locations`}>
                        ⚠ {u.unique_ip_count} locations
                      </span>
                    )}
                  </div>
                </td>
                <td>
                  {u.role !== 'admin' && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleToggleDisable(u._id, u.is_disabled)}
                        disabled={disablingId === u._id}
                        title={u.is_disabled ? 'Enable account' : 'Disable login'}
                        className={`px-2 py-1 rounded text-xs font-medium border-0 cursor-pointer transition-colors ${
                          u.is_disabled
                            ? 'bg-amber-100 text-amber-800 hover:bg-green-100 hover:text-green-800'
                            : 'bg-[#F4EFE6] text-[#78716C] hover:bg-amber-100 hover:text-amber-800'
                        }`}
                      >
                        {disablingId === u._id ? '...' : u.is_disabled ? 'Enable' : 'Disable'}
                      </button>
                      <button
                        onClick={() => handleDeleteUser(u._id, u.name)}
                        disabled={deletingId === u._id}
                        title="Permanently delete user"
                        className="px-2 py-1 rounded text-xs font-medium border-0 cursor-pointer bg-red-50 text-red-600 hover:bg-red-600 hover:text-white transition-colors"
                      >
                        {deletingId === u._id ? '...' : 'Delete'}
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Progress Tab
function ProgressTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [detail, setDetail] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState({ key: 'name', dir: 1 });

  useEffect(() => {
    adminApi.getProgressOverview()
      .then(r => setRows(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const toggleExpand = async (userId) => {
    if (expanded === userId) { setExpanded(null); setDetail([]); return; }
    setExpanded(userId);
    setDetailLoading(true);
    try {
      const r = await adminApi.getUserProgress(userId);
      setDetail(r.data);
    } catch { setDetail([]); }
    finally { setDetailLoading(false); }
  };

  const handleSort = (key) => {
    setSort(s => ({ key, dir: s.key === key ? -s.dir : 1 }));
  };

  const SortIcon = ({ k }) => sort.key === k
    ? (sort.dir === 1 ? <ChevronUp className="inline h-3 w-3 ml-1" /> : <ChevronDown className="inline h-3 w-3 ml-1" />)
    : null;

  const filtered = rows
    .filter(r => !search || r.name.toLowerCase().includes(search.toLowerCase()) || r.email.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      const av = a[sort.key] ?? '';
      const bv = b[sort.key] ?? '';
      return typeof av === 'number' ? (av - bv) * sort.dir : String(av).localeCompare(String(bv)) * sort.dir;
    });

  const totalCompleted = rows.reduce((s, r) => s + r.completed_sessions, 0);
  const avgProgress = rows.length ? Math.round(rows.reduce((s, r) => s + r.progress_percent, 0) / rows.length) : 0;
  const notStarted = rows.filter(r => r.completed_sessions === 0).length;
  const finished = rows.filter(r => r.completed_sessions === 21).length;

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <h1 className="font-heading text-3xl font-bold text-[#292524] mb-2">Student Progress</h1>
      <p className="text-[#78716C] mb-6">{rows.length} registered students</p>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Avg Progress', value: `${avgProgress}%` },
          { label: 'Sessions Completed', value: totalCompleted },
          { label: 'Not Started', value: notStarted },
          { label: 'Finished All 21', value: finished },
        ].map(c => (
          <div key={c.label} className="bg-white rounded-xl border border-[#E7E5E4] p-4 text-center">
            <p className="text-2xl font-bold text-[#9A3B26]">{c.value}</p>
            <p className="text-sm text-[#78716C] mt-1">{c.label}</p>
          </div>
        ))}
      </div>

      {/* Search */}
      <Input
        placeholder="Search by name or email..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        className="max-w-sm mb-4"
      />

      {/* Table */}
      <div className="bg-white rounded-xl border border-[#E7E5E4] overflow-hidden">
        <table className="data-table">
          <thead>
            <tr>
              <th className="cursor-pointer select-none" onClick={() => handleSort('name')}>Name <SortIcon k="name" /></th>
              <th className="cursor-pointer select-none" onClick={() => handleSort('email')}>Email <SortIcon k="email" /></th>
              <th className="cursor-pointer select-none text-center" onClick={() => handleSort('completed_sessions')}>Sessions <SortIcon k="completed_sessions" /></th>
              <th>Progress</th>
              <th className="cursor-pointer select-none" onClick={() => handleSort('last_active')}>Last Active <SortIcon k="last_active" /></th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(r => (
              <>
                <tr key={r.user_id} className="cursor-pointer hover:bg-[#FDFBF7]" onClick={() => toggleExpand(r.user_id)}>
                  <td className="font-medium">{r.name}</td>
                  <td className="text-[#78716C] text-sm">{r.email}</td>
                  <td className="text-center">
                    <span className={`font-semibold ${r.completed_sessions === 21 ? 'text-[#2C5E4B]' : r.completed_sessions === 0 ? 'text-[#78716C]' : 'text-[#9A3B26]'}`}>
                      {r.completed_sessions} / 21
                    </span>
                  </td>
                  <td style={{ minWidth: 140 }}>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-[#F4EFE6] rounded-full h-2">
                        <div
                          className="h-2 rounded-full bg-[#9A3B26] transition-all"
                          style={{ width: `${r.progress_percent}%` }}
                        />
                      </div>
                      <span className="text-xs text-[#78716C] w-8 text-right">{r.progress_percent}%</span>
                    </div>
                  </td>
                  <td className="text-sm text-[#78716C]">
                    {r.last_active ? new Date(r.last_active).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '—'}
                  </td>
                  <td className="text-[#78716C]">
                    {expanded === r.user_id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </td>
                </tr>

                {/* Expanded day-by-day detail */}
                {expanded === r.user_id && (
                  <tr key={`${r.user_id}-detail`}>
                    <td colSpan={6} className="bg-[#FDFBF7] px-6 py-4">
                      {detailLoading ? (
                        <p className="text-sm text-[#78716C]">Loading…</p>
                      ) : (
                        <div>
                          <p className="text-sm font-medium text-[#292524] mb-3">Day-by-day breakdown</p>
                          <div className="grid grid-cols-7 gap-2">
                            {detail.map(d => (
                              <div key={d.day} className={`rounded-lg p-2 text-center border ${
                                d.completed ? 'bg-[#2C5E4B] border-[#2C5E4B] text-white' :
                                d.progress_percent > 0 ? 'bg-[#FEF9EF] border-[#9A3B26] text-[#9A3B26]' :
                                'bg-white border-[#E7E5E4] text-[#78716C]'
                              }`}>
                                <p className="text-xs font-bold">Day {d.day}</p>
                                <p className="text-xs mt-0.5">
                                  {d.completed ? '✓ Done' : d.progress_percent > 0 ? `${Math.round(d.progress_percent)}%` : 'Not started'}
                                </p>
                                {d.completed_at && (
                                  <p className="text-xs opacity-70 mt-0.5">
                                    {new Date(d.completed_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="text-center py-8 text-[#78716C]">No students found.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Payments Tab
function PaymentsTab() {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRegistrations = async () => {
      try {
        const response = await adminApi.getRegistrations();
        setRegistrations(response.data);
      } catch (error) {
        console.error('Failed to fetch registrations:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchRegistrations();
  }, []);

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <h1 className="font-heading text-3xl font-bold text-[#292524] mb-8">Payments & Registrations</h1>
      
      <div className="bg-white rounded-xl border border-[#E7E5E4] overflow-hidden">
        {registrations.length === 0 ? (
          <div className="p-12 text-center text-[#78716C]">
            No registrations yet.
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Package</th>
                <th>Amount</th>
                <th>Gateway</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {registrations.map((reg) => (
                <tr key={reg._id}>
                  <td className="font-medium">{reg.user_email}</td>
                  <td>{reg.package_id}</td>
                  <td>{reg.currency} {reg.amount}</td>
                  <td>
                    <span className="px-2 py-1 bg-[#F4EFE6] rounded text-sm capitalize">
                      {reg.payment_gateway}
                    </span>
                  </td>
                  <td className="text-sm text-[#78716C]">
                    {reg.registered_at ? new Date(reg.registered_at).toLocaleDateString() : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// Settings Tab
function SettingsTab() {
  const [settings, setSettings] = useState({
    start_date: '',
    start_time_ist: '06:00',
    time_slots: [{ time: '6:00 - 7:30 AM', timezone: 'IST' }],
    languages: ['English', 'Malayalam'],
    level: 'Beginner',
    recording_access_hours: 24
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await adminApi.getSettings();
        if (response.data && Object.keys(response.data).length > 0) {
          const d = response.data;
          // Convert stored UTC ISO back to IST date + time for display
          let startDateLocal = '';
          let startTimeIst = '06:00';
          if (d.start_date) {
            const utc = new Date(d.start_date);
            // IST = UTC + 5h30m
            const ist = new Date(utc.getTime() + (5 * 60 + 30) * 60 * 1000);
            startDateLocal = ist.toISOString().split('T')[0];
            startTimeIst = ist.toISOString().split('T')[1].slice(0, 5);
          }
          setSettings((prev) => ({ ...prev, ...d, start_date: startDateLocal, start_time_ist: startTimeIst }));
        }
      } catch (error) {
        console.error('Failed to fetch settings:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Convert IST date+time to UTC ISO for storage
      const [h, m] = (settings.start_time_ist || '06:00').split(':').map(Number);
      const istOffsetMs = (5 * 60 + 30) * 60 * 1000;
      const istMidnight = new Date(settings.start_date + 'T00:00:00Z').getTime();
      const utcMs = istMidnight + h * 3600000 + m * 60000 - istOffsetMs;
      await adminApi.saveSettings({
        ...settings,
        start_date: new Date(utcMs).toISOString()
      });
      alert('Settings saved!');
    } catch (error) {
      console.error('Failed to save settings:', error);
      alert('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <h1 className="font-heading text-3xl font-bold text-[#292524] mb-8">Programme Settings</h1>
      
      <div className="bg-white rounded-xl border border-[#E7E5E4] p-6 max-w-2xl">
        <div className="space-y-6">
          <div className="space-y-2">
            <Label>Programme Start Date</Label>
            <Input
              type="date"
              value={settings.start_date}
              onChange={(e) => setSettings({ ...settings, start_date: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label>Session Start Time (IST)</Label>
            <Input
              type="time"
              value={settings.start_time_ist}
              onChange={(e) => setSettings({ ...settings, start_time_ist: e.target.value })}
            />
            <p className="text-xs text-[#78716C]">This sets the countdown target. Enter in IST (e.g. 06:00 for 6:00 AM IST).</p>
          </div>

          <div className="space-y-2">
            <Label>Level</Label>
            <Input
              value={settings.level}
              onChange={(e) => setSettings({ ...settings, level: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label>Recording Access (Hours)</Label>
            <Input
              type="number"
              value={settings.recording_access_hours}
              onChange={(e) => setSettings({ ...settings, recording_access_hours: parseInt(e.target.value) })}
            />
          </div>

          <div className="space-y-2">
            <Label>Languages (comma-separated)</Label>
            <Input
              value={settings.languages?.join(', ')}
              onChange={(e) => setSettings({ ...settings, languages: e.target.value.split(',').map(l => l.trim()) })}
            />
          </div>

          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="bg-[#9A3B26] hover:bg-[#7A2E1E]"
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function LiveSessionsTab() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    adminApi.getLiveSessions()
      .then((res) => setSessions(res.data || []))
      .catch((err) => setError(formatApiError(err)))
      .finally(() => setLoading(false));
  }, []);

  const handleCreate = async (payload) => {
    setSaving(true);
    try {
      const res = await adminApi.createLiveSession(payload);
      setSessions((prev) => [...prev, res.data]);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (id, payload) => {
    setSaving(true);
    try {
      await adminApi.updateLiveSession(id, payload);
      setSessions((prev) =>
        prev.map((s) => (s._id === id || s.id === id ? { ...s, ...payload } : s))
      );
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this session? This cannot be undone.')) return;
    setSaving(true);
    try {
      await adminApi.deleteLiveSession(id);
      setSessions((prev) => prev.filter((s) => (s._id || s.id) !== id));
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">{error}</div>
      )}
      <AdminSessionManager
        sessions={sessions}
        loading={loading}
        saving={saving}
        onCreateSession={handleCreate}
        onUpdateSession={handleUpdate}
        onDeleteSession={handleDelete}
      />
    </div>
  );
}

// Blog Tab
function BlogTab() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loadingPostId, setLoadingPostId] = useState(null);

  useEffect(() => { loadPosts(); }, []);

  const loadPosts = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.getBlogs();
      setPosts(Array.isArray(data) ? data : []);
    } catch { } finally { setLoading(false); }
  };

  const handleNew = () => setEditing({
    title: '', slug: '', excerpt: '', content: '',
    hero_image_url: '', hero_image_lqip: '', category: 'General',
    tags: '', author: 'The Kalari Movement', published: false, read_time_mins: '',
  });

  const handleEdit = async (post) => {
    setLoadingPostId(post._id);
    try {
      const { data } = await adminApi.getBlog(post._id);
      setEditing({
        ...data,
        content: data.content || '',
        tags: (data.tags || []).join(', '),
        hero_image_url: data.hero_image_url || '',
        hero_image_lqip: data.hero_image_lqip || '',
        read_time_mins: data.read_time_mins ?? '',
      });
    } catch (e) {
      alert('Failed to load post: ' + (e.response?.data?.detail || e.message || 'Unknown error'));
    } finally {
      setLoadingPostId(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this post? This cannot be undone.')) return;
    try {
      await adminApi.deleteBlog(id);
      loadPosts();
    } catch { alert('Delete failed'); }
  };

  const handleSave = async () => {
    const title = editing.title || '';
    if (!title.trim()) { alert('Title is required'); return; }
    setSaving(true);
    try {
      const payload = {
        title: editing.title || '',
        slug: editing.slug || '',
        excerpt: editing.excerpt || '',
        content: editing.content || '',
        hero_image_url: editing.hero_image_url || null,
        hero_image_lqip: editing.hero_image_lqip || null,
        category: editing.category || 'General',
        tags: editing.tags ? editing.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        author: editing.author || 'The Kalari Movement',
        published: !!editing.published,
        read_time_mins: editing.read_time_mins ? Number(editing.read_time_mins) : null,
      };
      if (editing._id) {
        await adminApi.updateBlog(editing._id, payload);
      } else {
        await adminApi.createBlog(payload);
      }
      setEditing(null);
      loadPosts();
    } catch (e) { alert('Save failed: ' + formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (editing !== null) {
    return (
      <BlogEditor
        editing={editing}
        setEditing={setEditing}
        onSave={handleSave}
        saving={saving}
        onCancel={() => setEditing(null)}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-2xl font-bold text-[#292524]">Blog Posts</h2>
        <Button onClick={handleNew} className="bg-[#9A3B26] hover:bg-[#7A2E1E] text-white">
          <Plus className="h-4 w-4 mr-2" /> New Post
        </Button>
      </div>
      {loading ? <LoadingSpinner /> : posts.length === 0 ? (
        <div className="text-center py-20 text-[#A8A29E]">No posts yet. Create your first post.</div>
      ) : (
        <div className="space-y-3">
          {posts.map(p => (
            <div key={p._id} className="bg-white rounded-xl border border-[#E7E5E4] p-5 flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center flex-wrap gap-2 mb-1">
                  <h3 className="font-heading font-bold text-[#292524] truncate">{p.title}</h3>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${p.published ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                    {p.published ? 'Published' : 'Draft'}
                  </span>
                  {p.category && <span className="text-xs text-[#A8A29E] bg-[#F4EFE6] px-2 py-0.5 rounded-full">{p.category}</span>}
                </div>
                <p className="text-sm text-[#78716C] truncate">{p.excerpt}</p>
                <p className="text-xs text-[#A8A29E] mt-0.5">
                  {p.read_time_mins ? `${p.read_time_mins} min read · ` : ''}
                  {p.created_at ? new Date(p.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
                  {p.slug && <span className="ml-2 font-mono">/blog/{p.slug}</span>}
                </p>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button
                  variant="outline" size="sm"
                  onClick={() => handleEdit(p)}
                  disabled={loadingPostId === p._id}
                >
                  {loadingPostId === p._id
                    ? <div className="h-4 w-4 border-2 border-[#9A3B26] border-t-transparent rounded-full animate-spin" />
                    : <Edit className="h-4 w-4" />}
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleDelete(p._id)} className="text-red-500 border-red-200 hover:bg-red-50">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function BlogEditor({ editing, setEditing, onSave, saving, onCancel }) {
  const contentRef = useRef(null);
  const contentImgRef = useRef(null);
  const [uploadingImg, setUploadingImg] = useState(false);

  const set = (key) => (e) => {
    const value = e && e.target ? e.target.value : e;
    setEditing(prev => ({ ...prev, [key]: value }));
  };

  const handleTitleChange = (val) => {
    const slug = val.toLowerCase().replace(/[^\w\s-]/g, '').replace(/[\s_]+/g, '-').replace(/^-+|-+$/, '');
    setEditing(prev => ({ ...prev, title: val, slug: prev._id ? prev.slug : slug }));
  };

  const insertTag = (open, close = '') => {
    const el = contentRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const selected = el.value.substring(start, end) || 'text';
    const replacement = open + selected + close;
    const newVal = el.value.substring(0, start) + replacement + el.value.substring(end);
    setEditing(prev => ({ ...prev, content: newVal }));
    setTimeout(() => {
      el.focus();
      el.selectionStart = start + open.length;
      el.selectionEnd = start + open.length + selected.length;
    }, 0);
  };

  const handleContentImage = async (file) => {
    if (!file) return;
    const cursorPos = contentRef.current ? contentRef.current.selectionStart : (editing.content || '').length;
    setUploadingImg(true);
    try {
      const { dataUrl, lqip } = await compressImage(file, 1200);
      const byteString = atob(dataUrl.split(',')[1]);
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);
      const blob = new Blob([ab], { type: 'image/webp' });
      const { data } = await adminApi.getUploadUrl();
      await fetch(data.upload_url, { method: 'PUT', body: blob, headers: { 'Content-Type': 'image/webp' } });
      const imgTag = `\n<img src="${data.public_url}" alt="" style="max-width:100%;height:auto;border-radius:8px;margin:16px 0">\n`;
      setEditing(prev => ({
        ...prev,
        content: prev.content.substring(0, cursorPos) + imgTag + prev.content.substring(cursorPos),
      }));
    } catch (err) { alert('Image upload failed: ' + err.message); }
    finally { setUploadingImg(false); contentImgRef.current && (contentImgRef.current.value = ''); }
  };

  const toolbarButtons = [
    { label: 'B', open: '<strong>', close: '</strong>' },
    { label: 'I', open: '<em>', close: '</em>' },
    { label: 'H2', open: '<h2>', close: '</h2>' },
    { label: 'H3', open: '<h3>', close: '</h3>' },
    { label: 'P', open: '<p>', close: '</p>' },
    { label: '"', open: '<blockquote>', close: '</blockquote>' },
    { label: 'UL', open: '<ul>\n<li>', close: '</li>\n</ul>' },
    { label: 'OL', open: '<ol>\n<li>', close: '</li>\n</ol>' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" onClick={onCancel} className="text-[#78716C] hover:text-[#292524]">
          ← Back to Posts
        </Button>
        <h2 className="font-heading text-2xl font-bold text-[#292524]">
          {editing._id ? 'Edit Post' : 'New Post'}
        </h2>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left: main content */}
        <div className="lg:col-span-2 space-y-4">
          <div className="space-y-1.5">
            <Label>Title <span className="text-red-500">*</span></Label>
            <Input value={editing.title} onChange={(e) => handleTitleChange(e.target.value)} placeholder="Post title" className="text-lg" />
          </div>

          <div className="space-y-1.5">
            <Label>URL Slug</Label>
            <Input value={editing.slug} onChange={set('slug')} placeholder="auto-generated-from-title" className="font-mono text-sm text-[#78716C]" />
            <p className="text-xs text-[#A8A29E]">Will be accessible at /blog/{editing.slug || '...'}</p>
          </div>

          <div className="space-y-1.5">
            <Label>Excerpt</Label>
            <Textarea value={editing.excerpt} onChange={set('excerpt')} placeholder="Short description shown on the blog index (2–3 sentences)" rows={3} />
          </div>

          {/* HTML content editor */}
          <div className="space-y-1.5">
            <Label>Content (HTML)</Label>
            <div className="border border-[#E7E5E4] rounded-xl overflow-hidden">
              <div className="flex flex-wrap gap-1 p-2 bg-[#F9F6F2] border-b border-[#E7E5E4]">
                {toolbarButtons.map(({ label, open, close }) => (
                  <button
                    key={label} type="button"
                    onClick={() => insertTag(open, close)}
                    className="px-2 py-1 text-xs font-bold text-[#292524] bg-white hover:bg-[#E7E5E4] border border-[#E7E5E4] rounded transition-colors"
                  >
                    {label}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => { const url = window.prompt('Link URL (include https://):', 'https://'); if (url) insertTag(`<a href="${url}" target="_blank" rel="noopener noreferrer">`, '</a>'); }}
                  className="px-2 py-1 text-xs font-bold text-[#292524] bg-white hover:bg-[#E7E5E4] border border-[#E7E5E4] rounded transition-colors"
                >
                  Link
                </button>
                <button
                  type="button"
                  onClick={() => contentImgRef.current?.click()}
                  disabled={uploadingImg}
                  className="px-2 py-1 text-xs font-bold text-[#9A3B26] bg-white hover:bg-[#F4EFE6] border border-[#9A3B26]/30 rounded transition-colors flex items-center gap-1"
                >
                  <Image className="h-3 w-3" />
                  {uploadingImg ? 'Uploading…' : 'Insert Image'}
                </button>
                <input
                  ref={contentImgRef} type="file" accept="image/*,.heic,.heif" className="hidden"
                  onChange={(e) => handleContentImage(e.target.files?.[0])}
                />
              </div>
              <textarea
                ref={contentRef}
                value={editing.content}
                onChange={set('content')}
                rows={20}
                className="w-full p-3 font-mono text-sm text-[#292524] bg-white focus:outline-none resize-y leading-relaxed"
                placeholder="<p>Write your post content in HTML...</p>&#10;<p>Use the toolbar above to format text, or type HTML directly.</p>"
              />
            </div>
          </div>
        </div>

        {/* Right: settings sidebar */}
        <div className="space-y-4">
          {/* Publish + Save */}
          <div className="bg-white rounded-xl border border-[#E7E5E4] p-4 space-y-4">
            <h3 className="font-semibold text-[#292524] text-sm uppercase tracking-wide">Publish</h3>
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#78716C]">{editing.published ? 'Published' : 'Draft'}</span>
              <button
                type="button"
                onClick={() => setEditing(prev => ({ ...prev, published: !prev.published }))}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${editing.published ? 'bg-green-500' : 'bg-[#E7E5E4]'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${editing.published ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>
            <Button onClick={onSave} disabled={saving} className="w-full bg-[#9A3B26] hover:bg-[#7A2E1E] text-white">
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving…' : 'Save Post'}
            </Button>
          </div>

          {/* Hero image */}
          <div className="bg-white rounded-xl border border-[#E7E5E4] p-4 space-y-3">
            <h3 className="font-semibold text-[#292524] text-sm uppercase tracking-wide">Hero Image</h3>
            <ImageUploadField
              value={editing.hero_image_url}
              onChange={(url) => setEditing(prev => ({ ...prev, hero_image_url: url }))}
              onLqipChange={(lqip) => setEditing(prev => ({ ...prev, hero_image_lqip: lqip }))}
              label="Upload hero image"
              maxWidth={1200}
            />
          </div>

          {/* Category */}
          <div className="bg-white rounded-xl border border-[#E7E5E4] p-4 space-y-2">
            <Label className="text-sm font-semibold text-[#292524] uppercase tracking-wide">Category</Label>
            <Input value={editing.category} onChange={set('category')} placeholder="General" />
          </div>

          {/* Tags */}
          <div className="bg-white rounded-xl border border-[#E7E5E4] p-4 space-y-2">
            <Label className="text-sm font-semibold text-[#292524] uppercase tracking-wide">Tags</Label>
            <Input value={editing.tags} onChange={set('tags')} placeholder="breath, practice, philosophy" />
            <p className="text-xs text-[#A8A29E]">Comma-separated</p>
          </div>

          {/* Author + Read time */}
          <div className="bg-white rounded-xl border border-[#E7E5E4] p-4 space-y-3">
            <h3 className="font-semibold text-[#292524] text-sm uppercase tracking-wide">Author</h3>
            <Input value={editing.author} onChange={set('author')} placeholder="The Kalari Movement" />
            <Label className="text-xs text-[#A8A29E]">Read time (mins, auto-calculated if blank)</Label>
            <Input type="number" value={editing.read_time_mins} onChange={set('read_time_mins')} placeholder="auto" min="1" />
          </div>
        </div>
      </div>
    </div>
  );
}

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center p-12">
      <div className="animate-spin rounded-full h-8 w-8 border-4 border-[#9A3B26] border-t-transparent"></div>
    </div>
  );
}
